import clsx from 'clsx';
import { CookieConsentPopup } from 'components/layout/cookie-consent/CookieConsentPopup';
import ReferAFriendBanner from 'components/layout/header2026/banner/ReferAFriend/ReferAFriendBanner';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import { OutsideUKPopup } from 'components/OutsideUkPopup';
import { InviteFriendsModal } from 'components/referrals/2026/invite-friends/inviteFriendsModal';
import Toast from 'components/Toast';
import UpgradePopup from 'components/UpgradePopup/UpgradePopup';
import { useABTestStore } from 'hooks/stores/useABTestStore';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import React, { useEffect, useRef, useState } from 'react';
import { DASHBOARD_LINK } from 'stores/UIStore';
import { getEnterFlowProgressValue } from 'utils/ab-test/abTests';
import { trackEvent } from 'utils/analytics/analytics';
import { extendClassName } from 'utils/common';
import {
  ASSET_PREFIX,
  colors,
  contentMaxWidth,
  runningLocally,
} from 'utils/constants';
import Footer from './footer/Footer';
import Header from './header2026';
import VideoOverlay from './video-overlay/VideoOverlay';

interface IProps {
  additionalClassName?: string;
  limitItemWidths?: boolean;
  paddedMaxWidthContainer?: string;
  drawCounterInHeader?: boolean;
  children: React.ReactNode;
  hideHeaderOnMobile?: boolean;
  noHeaderDropShadow?: boolean;
}

/** Maintain a list of routes that power the enter flow. These pages used to be in plasmic, and
 * MainLayout was previously the only place where we can get common code to run. This approach
 * could change now that we no longer use plasmic
 */
const enterFlowRoutes = [
  '/enter',
  '/enternow/1',
  '/enternow/2',
  '/enternow/3',
  '/enternow/4',
  '/enternow/5',
  '/enternow/5?redirect=checkout',
  '/enter/payment',
  '/checkout',
];

/** Once we complete checkout, it is possible for people to re-enter the checkout flow by visiting pages from their
 * history or using buttons around the site. We need to clear order metadata when re-entering the flow
 */
const routesThatShouldResetEnterStoreOrderInformation = [...enterFlowRoutes];

const MainLayout = observer(
  ({
    additionalClassName = '',
    paddedMaxWidthContainer = '',
    limitItemWidths,
    children,
    hideHeaderOnMobile,
    noHeaderDropShadow,
  }: IProps) => {
    const router = useRouter();

    const {
      toast,
      showVideoOverlay,
      videoOverlayType,
      videoOverlayLink,
      displayToast,
      displayHeaderBanner,
      setShowVideoOverlay,
      removeVideoOverlayLink,
      navigateToAccount,
      inviteFriendsModalVisible,
      invitedFriendModalHasReachedReferralLimit,
      isEligibleToRefer,
      setShowInviteFriendsModal,
    } = useUIStore();

    const { currentUser, outsideUK, subscriptionStatus } = useUserStore();

    const enterStore = useEnterStore();
    const { referralDetails } = enterStore;

    const abTestStore = useABTestStore();

    const routerCurrentPath = router.asPath.split('?')[0].split('#')[0];
    const headerHidden = ['/play-free'].includes(router.pathname);
    const footerHidden = [
      '/play-free',
      '/checkout',
      ...enterFlowRoutes,
    ].includes(routerCurrentPath);

    const enterFlowProgressVariant = getEnterFlowProgressValue();
    const staticHeader = Boolean(
      enterFlowProgressVariant &&
        enterFlowProgressVariant != 'CONTROL' &&
        enterFlowRoutes.includes(routerCurrentPath),
    );

    const tokenTownPage = router.pathname.startsWith('/token-town');
    const [showOutsideUK, setShowOutsideUk] = useState(false);
    const outsideUkRef = useRef<any>(null);

    const showPremiumUpgradePrompt =
      !router.pathname.startsWith('/admin') &&
      !router.pathname.startsWith('/confirmation') &&
      !tokenTownPage &&
      !router.pathname.startsWith('/account/subscriptions/upgrade') &&
      !router.pathname.startsWith('/enter');

    const showOutsideUkPopup = async () => {
      if (outsideUK) {
        setShowOutsideUk(true);
        trackEvent('Shown outside uk Popup');
      }
    };

    useEffect(() => {
      // Part of the enter store needs clearing when we visit any of the entry pages before payment. Since those pages
      // are either in Plasmic or NextJS code, we centralise this logic here.

      if (
        routerCurrentPath.startsWith('/enter/payment') ||
        routerCurrentPath.startsWith('/checkout')
      ) {
        // Don't clear anything if we're on a payment page
        return;
      }

      for (const path of routesThatShouldResetEnterStoreOrderInformation) {
        if (routerCurrentPath === path) {
          let enterStoreChanged = false;
          if (enterStore.orderCompleted) {
            enterStore.setOrderCompleted(false);
            enterStoreChanged = true;
          }
          if (enterStore.orderError) {
            enterStore.setOrderError(null);
            enterStoreChanged = true;
          }
          if (enterStoreChanged) {
            console.log(
              'We have reentered the enter flow. Clearing order information.',
            );
          }
          break;
        }
      }
    }, [routerCurrentPath]);

    useEffect(() => {
      if (referralDetails) {
        displayHeaderBanner({
          faIconName: null,
          customContent: (
            <ReferAFriendBanner
              referrerFirstName={referralDetails.firstName}
              referrerAvatarLocation={
                referralDetails.avatarLocation ?? undefined
              }
            />
          ),
          tapHandler: null,
        });
      } else {
        displayHeaderBanner(null);
      }
    }, [referralDetails]);

    useEffect(() => {
      if (router.asPath === '/' && subscriptionStatus) {
        if (router.query.redirect !== 'false') {
          router.push(DASHBOARD_LINK);
        }
      }
    }, [router.query.redirect, router.asPath, subscriptionStatus]);

    useEffect(() => {
      if (routerCurrentPath === '/' && router.asPath.includes('loggedout')) {
        displayToast({
          title: 'You have successfully logged out.',
          timeout: 3500,
        });
      }
    }, []);

    useEffect(() => {
      showOutsideUkPopup();
    }, [router.asPath, outsideUK]);

    return (
      <>
        <div
          className={clsx(
            'contentContainer',
            runningLocally && 'daymadeLocalDev',
          )}
        >
          <div
            className={extendClassName('mainLayout', [
              additionalClassName,
              limitItemWidths ? 'mainLayoutWithMaxWidth' : undefined,
            ])}
          >
            <Head>
              {/* PWA */}
              <link rel="manifest" href="/pwa/manifest.json" />
              <meta name="apple-mobile-web-app-capable" content="yes" />

              {/* Icons */}
              <link
                rel="icon"
                type="image/x-icon"
                href={`${ASSET_PREFIX}/static/favicon.ico?v=2`}
              />
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href={`${ASSET_PREFIX}/static/apple-touch-icon.png?v=3`}
              />

              {/* Instagram Embed Script */}
            </Head>
            <Script
              id="instagram-embed-script"
              async
              defer
              src="https://www.instagram.com/embed.js"
            ></Script>
            {!headerHidden && <Header hideOnMobile={hideHeaderOnMobile} />}

            {showPremiumUpgradePrompt && <UpgradePopup />}

            <VideoOverlay
              showVideoOverlay={showVideoOverlay}
              onClose={() => {
                setShowVideoOverlay(false);
                removeVideoOverlayLink();
              }}
              videoUrl={videoOverlayLink}
              videoType={videoOverlayType}
            />

            {paddedMaxWidthContainer ? (
              <PaddedMaxWidthContainer>{children}</PaddedMaxWidthContainer>
            ) : (
              children
            )}
            {!footerHidden && <Footer />}
          </div>

          {toast && <Toast toast={toast} />}

          {inviteFriendsModalVisible && currentUser?.referralId && (
            <InviteFriendsModal
              referralId={currentUser?.referralId}
              handleClose={() => setShowInviteFriendsModal(false)}
              isEligibleToRefer={isEligibleToRefer}
              hasReachedReferralLimit={
                invitedFriendModalHasReachedReferralLimit
              }
            />
          )}

          <CookieConsentPopup CookieConsentStyle={'legacy'} />

          {showOutsideUK && (
            <OutsideUKPopup onClose={() => setShowOutsideUk(false)} />
          )}

          <style jsx global>
            {`
              body .light-grey {
                color: ${colors.primaryBrandGray}99;
              }

              :global(.ui.modal .ui.primary.button) {
                background-color: ${colors.primaryBrandGray};
              }

              :global(.ui.modal .ui.primary.button:hover) {
                background-color: ${colors.primaryBrandGrayPressed};
              }

              .paddedMaxWidthContainer {
                max-width: ${contentMaxWidth};
              }

              .sectionContainer {
                background: url('${ASSET_PREFIX}/images/landing/waves.svg');
              }

              button {
                background-color: ${colors.primaryBrandGray};
              }

              button:hover {
                background-color: ${colors.primaryBrandGrayPressed};
                color: ${colors.primaryBrandGray};
              }

              .greenOutlineText {
                -webkit-text-stroke-color: ${colors.trees.neon};
                text-shadow:
                  0 0 10px ${`${colors.trees.neon}70`},
                  0 0 5px ${colors.trees.shadow};
              }

              .greyOutlineText {
                -webkit-text-stroke-color: ${colors.primaryBrandGray};
              }

              @media (hover: none) and (pointer: coarse) {
                // Hide the scroll buttons if using a touch screen.
                :global(.scrollButton) {
                  display: none;
                }
              }

              :global(#form, #errorMessage) {
                display: flex;
                flex-direction: column;
                max-width: 500px;
                width: 100%;
              }

              :global(#form) {
                padding: 1em 0;
              }

              :global(#errorMessage) {
                padding: 1em;
              }

              :global(#form input) {
                font-size: 16px;
                border: 3px solid #e3e3e3;
              }

              :global(.bigFirstLetter) {
                text-shadow: 4px 4px ${colors.brand.neon};
                font-weight: bold;
                font-size: 45px;
              }

              :global(.scrollButton) {
                position: absolute;
                background-color: transparent;
                transition: 0.2s;
              }

              :global(.scrollButton.scalable:hover) {
                transform: scale(1.1);
              }

              :global(.blogPost a:not(.backButton)) {
                text-decoration: underline;
                white-space: break-spaces;
              }

              .tokenTown h1,
              .tokenTown h2,
              .tokenTown input {
                color: ${colors.primaryBrandGray};
              }
            `}
          </style>

          <script
            dangerouslySetInnerHTML={{
              __html: `
          function isItIE() {
            user_agent = navigator.userAgent;
            var is_it_ie = user_agent.indexOf("MSIE ") > -1 || user_agent.indexOf("Trident/") > -1;
            return is_it_ie; 
          }
          
          if (isItIE()) {
            alert("DAYMADE unfortunately does not work well with Internet Explorer. Please try using a modern browser (such as Chrome, Firefox, Brave, Safari)."); 
          }
        `,
            }}
          />
        </div>
      </>
    );
  },
);

export default MainLayout;
